import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useCallback, useEffect, useState } from 'react'

import { OnboardingResponseStatus } from 'clickNgo/configs/service/onboarding'
import { updatePartner } from 'clickNgo/entities/partner/api'
import { IPartner } from 'clickNgo/entities/partner/models'
import { IPartnerFormBasic } from 'clickNgo/entities/partner/models/form/basic'
import { getJSendErrorMessages } from 'clickNgo/helpers/api'
import { resetModifiedDataByField, useGetFormDataField } from 'cng-common/features/form'
import { TForm } from 'cng-common/features/form/models'
import Logger from 'core/utils/Logger'

import {
  PartnerEditorFormKeys,
  PartnerEditorFormSettings,
  UpdatePartnerResult,
} from '../../../../configs'
import { IPartnerEditorData } from '../../../../models'

interface IUseEditPartnerBasicInfoHandlerParams {
  partner: IPartner
}

export const useEditPartnerBasicInfoHandler = ({
  partner,
}: IUseEditPartnerBasicInfoHandlerParams) => {
  const dispatch = useAppDispatch()
  const [errorMessages, setErrorMessages] = useState<string[]>([])
  const [actualData, updateActualData] = useGetFormDataField<IPartnerEditorData>(
    PartnerEditorFormKeys.Setting,
    PartnerEditorFormSettings.ActualData
  )
  const [currentMenuItem] = useGetFormDataField<IPartnerEditorData>(
    PartnerEditorFormKeys.Setting,
    PartnerEditorFormSettings.CurrentMenuItem
  )

  const [doCopyBasicAddress, , isDoCopyBasicAddressModified] = useGetFormDataField<boolean>(
    PartnerEditorFormKeys.Setting,
    PartnerEditorFormSettings.DoCopyBasicAddress
  )

  const handleOnPartnerBasicChangesApply = useCallback(
    async ({ formData, invalidFields, modifiedFields }: TForm<IPartnerFormBasic>) => {
      setErrorMessages([])
      const hasInvalidModifiedFields = Object.keys(invalidFields).some((invalidField) =>
        modifiedFields.includes(invalidField)
      )

      if (hasInvalidModifiedFields) {
        Logger.error('HAS INVALID FIELDS')
        return UpdatePartnerResult.Failed
      }

      let updatedPartnerData: IPartner = {
        ...partner,
        ...formData,
        modifiedValues: modifiedFields,
      }

      if (doCopyBasicAddress) {
        updatedPartnerData = {
          ...updatedPartnerData,
          legalAddressLine1: updatedPartnerData.addressLine1,
          legalAddressLine2: updatedPartnerData.addressLine2,
          legalAddressLine3: updatedPartnerData.addressLine3,
          legalCity: updatedPartnerData.city,
          legalCountry: updatedPartnerData.country,
          legalState: updatedPartnerData.state,
          legalZipCode: updatedPartnerData.zipCode,
          modifiedValues: [
            ...updatedPartnerData.modifiedValues,
            'LegalAddressLine1',
            'LegalAddressLine2',
            'LegalAddressLine3',
            'LegalCity',
            'LegalCountry',
            'LegalState',
            'LegalZipCode',
          ],
        }
      }

      const result = await updatePartner(updatedPartnerData)

      if (result.status === OnboardingResponseStatus.Success) {
        updateActualData({
          ...actualData,
          partner: {
            ...updatedPartnerData,
            entryId406: result.data.entryId406 || updatedPartnerData.entryId406,
            entryId502: result.data.entryId502 || updatedPartnerData.entryId502,
            legalWorkflowStatus: result.data.entryId502
              ? result.data.legalWorkflowStatus
              : updatedPartnerData.legalWorkflowStatus,
            workflowStatus: result.data.entryId406
              ? result.data.workflowStatus
              : updatedPartnerData.workflowStatus,
            legalModifiedValues: result.data.entryId502
              ? result.data.legalModifiedValues
              : updatedPartnerData.legalModifiedValues,
            modifiedValues: result.data.entryId406
              ? result.data.modifiedValues
              : updatedPartnerData.modifiedValues,
          },
        })
        return result.data.entryId406 ? UpdatePartnerResult.Requested : UpdatePartnerResult.Updated
      }

      if (result.status === OnboardingResponseStatus.Fail) {
        setErrorMessages(getJSendErrorMessages(result))
        return UpdatePartnerResult.Failed
      }

      if (result.status === OnboardingResponseStatus.Error) {
        setErrorMessages([result.message])
        return UpdatePartnerResult.Failed
      }

      setErrorMessages(['Unknown error'])
      return UpdatePartnerResult.Failed
    },
    [actualData, doCopyBasicAddress, partner, updateActualData]
  )

  const handleOnPartnerBasicChangesCancel = useCallback(
    ({ modifiedFields }: TForm<IPartnerFormBasic>) => {
      modifiedFields.forEach((field) => {
        dispatch(resetModifiedDataByField({ formKey: PartnerEditorFormKeys.BasicInfo, field }))
      })

      if (isDoCopyBasicAddressModified) {
        dispatch(
          resetModifiedDataByField({
            formKey: PartnerEditorFormKeys.Setting,
            field: PartnerEditorFormSettings.DoCopyBasicAddress,
            defaultValue: !doCopyBasicAddress,
          })
        )
      }
    },
    [dispatch, doCopyBasicAddress, isDoCopyBasicAddressModified]
  )

  useEffect(() => {
    return () => {
      setErrorMessages([])
    }
  }, [currentMenuItem])

  return { handleOnPartnerBasicChangesApply, handleOnPartnerBasicChangesCancel, errorMessages }
}
